// Unified, typed API client. Every network call in the app goes through here.
// When VITE_MOCK === "true" (default) it delegates to the in-memory mock so the
// UI runs with no backend; otherwise it hits the live backend via http.ts.
//
// Live wire contract (specs/03-api.md "Admin / control-plane API"):
//   - Control plane is under `/admin/*`, session-cookie authenticated, with
//     double-submit CSRF (token echoed by register/login/me, sent back on mutations).
//   - The dashboard data plane is project-scoped under
//     `/v1/projects/{project_id}/leaderboards/...` (session-authenticated; the
//     project UUID is resolved server-side to its board-namespace slug).
// This module is the ONLY place that adapts backend wire shapes to the UI's types
// and that branches mock vs. live.

import { http, setCsrfToken } from "./http";
import { mockApi } from "./mock";
import type {
  AccountCreated,
  ApiKey,
  ApiKeyCreated,
  BoardVersion,
  Branding,
  CreateBoardInput,
  Entry,
  FlaggedScore,
  KeyScope,
  Leaderboard,
  LeaderboardConfig,
  Member,
  PlanView,
  PlayerBoard,
  PlayerSubmission,
  PlayerSummary,
  Project,
  SubmitScoreResult,
  UsageView,
  User,
  ViewToken,
} from "./types";

export const IS_MOCK = (import.meta.env.VITE_MOCK ?? "true") !== "false";

// Project-scoped data-plane path helpers (live mode).
const p = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}`;
const lb = (projectId: string, id: string) =>
  `${p(projectId)}/leaderboards/${encodeURIComponent(id)}`;

// ---- backend wire shapes (control plane) -----------------------------------

interface AuthResponse {
  user_id: string;
  email: string;
  email_verified: boolean;
  csrf_token: string;
}
interface MeResponse extends AuthResponse {
  accounts: { account_id: string; name: string; role: string }[];
}
interface KeyView {
  id: string;
  project_id: string;
  project_slug: string;
  name: string;
  prefix: string;
  scopes: KeyScope[];
  rate_limit_per_min: number | null;
  usage_count: number;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}
interface CreatedKeyView extends KeyView {
  raw_key: string;
}

// The active account id (needed to create projects/keys) is captured from me()/login.
let activeAccountId: string | null = null;

function slugify(name: string): string {
  const s = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "project";
}

function toUser(a: AuthResponse, fallbackName?: string): User {
  setCsrfToken(a.csrf_token);
  return {
    id: a.user_id,
    email: a.email,
    name: fallbackName ?? a.email.split("@")[0] ?? a.email,
    email_verified: a.email_verified,
    created_at: new Date().toISOString(),
  };
}

function toApiKey(k: KeyView): ApiKey {
  return {
    id: k.id,
    name: k.name,
    prefix: `${k.prefix}…`,
    scopes: k.scopes,
    created_at: k.created_at,
    last_used_at: k.last_used_at,
    revoked_at: k.revoked_at,
  };
}

export const api = {
  isMock: IS_MOCK,

  // ---- auth ----
  login(email: string, password: string): Promise<User> {
    if (IS_MOCK) return mockApi.login(email, password);
    return http.post<AuthResponse>("/admin/login", { email, password }).then((a) => {
      // login response has no accounts; fetch them so project/key creation works.
      void api.me().catch(() => {});
      return toUser(a);
    });
  },
  register(email: string, name: string, password: string): Promise<User> {
    if (IS_MOCK) return mockApi.register(email, name, password);
    return http
      .post<AuthResponse>("/admin/register", { email, password, display_name: name })
      .then((a) => {
        void api.me().catch(() => {});
        return toUser(a, name);
      });
  },
  me(): Promise<User> {
    if (IS_MOCK) return mockApi.me();
    return http.get<MeResponse>("/admin/me").then((m) => {
      activeAccountId = m.accounts[0]?.account_id ?? null;
      return toUser(m);
    });
  },
  logout(): Promise<void> {
    if (IS_MOCK) return mockApi.logout();
    return http.post<void>("/admin/logout").then(() => {
      setCsrfToken(null);
      activeAccountId = null;
    });
  },
  // Re-send the email-verification link to the logged-in user's address. 202 on success.
  resendVerification(): Promise<void> {
    if (IS_MOCK) return Promise.resolve();
    return http.post<void>("/admin/resend-verification");
  },
  // Update the logged-in user's display name. Returns the updated User.
  updateProfile(name: string): Promise<User> {
    if (IS_MOCK) return mockApi.updateProfile(name);
    return http.patch<MeResponse>("/admin/me", { display_name: name }).then((m) => {
      activeAccountId = m.accounts[0]?.account_id ?? null;
      return toUser(m, name);
    });
  },
  // Change the logged-in user's password. Verifies current first.
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (IS_MOCK) return mockApi.changePassword(currentPassword, newPassword);
    return http
      .post<unknown>("/admin/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      })
      .then(() => undefined);
  },

  // ---- projects ----
  listProjects(): Promise<Project[]> {
    return IS_MOCK ? mockApi.listProjects() : http.get<Project[]>("/admin/projects");
  },
  async createProject(name: string): Promise<Project> {
    if (IS_MOCK) return mockApi.createProject(name);
    if (!activeAccountId) await api.me(); // ensure we know which account to create under
    if (!activeAccountId) throw new Error("No account available to create a project in");
    return http.post<Project>("/admin/projects", {
      account_id: activeAccountId,
      slug: slugify(name),
      name,
    });
  },
  async updateProject(projectId: string, patch: { name?: string; branding?: Branding }): Promise<Project> {
    if (IS_MOCK) return mockApi.updateProject(projectId, patch);
    return http.patch<Project>(`/admin/projects/${encodeURIComponent(projectId)}`, patch);
  },
  // List members for the account that owns the given project.
  listMembers(projectId: string): Promise<Member[]> {
    if (IS_MOCK) return mockApi.listMembers(projectId);
    return http
      .get<{ user_id: string; email: string; name: string; role: string }[]>(
        `/admin/projects/${encodeURIComponent(projectId)}/members`,
      )
      .then((rows) =>
        rows.map((r) => ({
          user_id: r.user_id,
          email: r.email,
          name: r.name,
          role: r.role as Member["role"],
        })),
      );
  },
  // Invite a user by email to the account that owns this project.
  inviteMember(
    accountId: string,
    email: string,
    role: Member["role"],
  ): Promise<Member> {
    if (IS_MOCK) return mockApi.inviteMember(accountId, email, role);
    return http
      .post<{ user_id: string; email: string; name: string; role: string }>(
        `/admin/accounts/${encodeURIComponent(accountId)}/members`,
        { email, role },
      )
      .then((r) => ({
        user_id: r.user_id,
        email: r.email,
        name: r.name,
        role: r.role as Member["role"],
      }));
  },

  // ---- api keys ----
  listKeys(projectId: string): Promise<ApiKey[]> {
    if (IS_MOCK) return mockApi.listKeys(projectId);
    return http
      .get<KeyView[]>(`/admin/projects/${encodeURIComponent(projectId)}/keys`)
      .then((ks) => ks.map(toApiKey));
  },
  createKey(projectId: string, name: string, scopes: KeyScope[]): Promise<ApiKeyCreated> {
    if (IS_MOCK) return mockApi.createKey(projectId, name, scopes);
    return http
      .post<CreatedKeyView>("/admin/keys", { project_id: projectId, name, scopes })
      .then((k) => ({ ...toApiKey(k), secret: k.raw_key }));
  },
  revokeKey(projectId: string, keyId: string): Promise<void> {
    return IS_MOCK
      ? mockApi.revokeKey(projectId, keyId)
      : http.del<void>(`/admin/keys/${encodeURIComponent(keyId)}`);
  },

  // ---- leaderboards (project-scoped data plane) ----
  listBoards(projectId: string): Promise<Leaderboard[]> {
    return IS_MOCK ? mockApi.listBoards(projectId) : http.get<Leaderboard[]>(`${p(projectId)}/leaderboards`);
  },
  getBoard(projectId: string, id: string): Promise<Leaderboard> {
    return IS_MOCK ? mockApi.getBoard(projectId, id) : http.get<Leaderboard>(lb(projectId, id));
  },
  createBoard(projectId: string, cfg: CreateBoardInput): Promise<Leaderboard> {
    return IS_MOCK ? mockApi.createBoard(projectId, cfg) : http.post<Leaderboard>(`${p(projectId)}/leaderboards`, cfg);
  },
  updateBoard(projectId: string, id: string, patch: Partial<LeaderboardConfig>): Promise<Leaderboard> {
    return IS_MOCK ? mockApi.updateBoard(projectId, id, patch) : http.patch<Leaderboard>(lb(projectId, id), patch);
  },
  deleteBoard(projectId: string, id: string): Promise<void> {
    return IS_MOCK ? mockApi.deleteBoard(projectId, id) : http.del<void>(lb(projectId, id));
  },
  // Seasons/versions are not implemented in the backend yet (Phase 9); show none in live mode.
  listVersions(projectId: string, id: string): Promise<BoardVersion[]> {
    return IS_MOCK ? mockApi.listVersions(projectId, id) : Promise.resolve([]);
  },

  // ---- entries / scores ----
  top(projectId: string, id: string, limit = 100, offset = 0): Promise<Entry[]> {
    return IS_MOCK ? mockApi.top(projectId, id, limit, offset) : http.get<Entry[]>(`${lb(projectId, id)}/top?limit=${limit}&offset=${offset}`);
  },
  count(projectId: string, id: string): Promise<number> {
    return IS_MOCK
      ? mockApi.count(projectId, id)
      : http.get<{ count: number }>(`${lb(projectId, id)}/count`).then((r) => r.count);
  },
  submitScore(projectId: string, id: string, playerId: string, score: number): Promise<SubmitScoreResult> {
    if (IS_MOCK) return mockApi.submitScore(projectId, id, playerId, score);
    // Backend SubmitResult is { player_id, score, rank, changed } — no previous_rank yet.
    return http
      .post<{ rank: number; score: number; changed: boolean; previous_rank?: number | null }>(
        `${lb(projectId, id)}/scores`,
        { player_id: playerId, score },
      )
      .then((r) => ({ rank: r.rank, score: r.score, changed: r.changed, previous_rank: r.previous_rank ?? null }));
  },
  deleteEntry(projectId: string, id: string, playerId: string): Promise<void> {
    return IS_MOCK ? mockApi.deleteEntry(projectId, id, playerId) : http.del<void>(`${lb(projectId, id)}/scores/${encodeURIComponent(playerId)}`);
  },

  // ---- moderation / anti-cheat (Gap 3) ----
  listFlagged(projectId: string, status?: string): Promise<FlaggedScore[]> {
    if (IS_MOCK) return mockApi.listFlagged(projectId, status);
    const q = status ? `&status=${encodeURIComponent(status)}` : "";
    return http.get<FlaggedScore[]>(`/admin/flagged?project_id=${encodeURIComponent(projectId)}${q}`);
  },
  resolveFlag(projectId: string, flagId: number, status: "approved" | "rejected"): Promise<FlaggedScore> {
    if (IS_MOCK) return mockApi.resolveFlag(projectId, flagId, status);
    return http.post<FlaggedScore>(`/admin/flagged/${flagId}/resolve`, { project_id: projectId, status });
  },

  // ---- players (player-centric view: players → boards → submissions) ----
  listPlayers(projectId: string, q?: string, limit?: number): Promise<PlayerSummary[]> {
    if (IS_MOCK) return mockApi.listPlayers(projectId, q, limit);
    const params = new URLSearchParams({ project_id: projectId });
    if (q) params.set("q", q);
    if (limit) params.set("limit", String(limit));
    return http.get<PlayerSummary[]>(`/admin/players?${params}`);
  },
  playerBoards(projectId: string, playerId: string): Promise<PlayerBoard[]> {
    if (IS_MOCK) return mockApi.playerBoards(projectId, playerId);
    return http.get<PlayerBoard[]>(
      `/admin/players/${encodeURIComponent(playerId)}/boards?project_id=${encodeURIComponent(projectId)}`,
    );
  },
  playerSubmissions(
    projectId: string,
    playerId: string,
    board: string,
    limit?: number,
  ): Promise<PlayerSubmission[]> {
    if (IS_MOCK) return mockApi.playerSubmissions(projectId, playerId, board, limit);
    const params = new URLSearchParams({ project_id: projectId, board });
    if (limit) params.set("limit", String(limit));
    return http.get<PlayerSubmission[]>(
      `/admin/players/${encodeURIComponent(playerId)}/submissions?${params}`,
    );
  },
  /** Mint a short-lived operator subscribe token to open live WS rank subscriptions. */
  viewToken(projectId: string): Promise<ViewToken> {
    if (IS_MOCK) return mockApi.viewToken(projectId);
    return http.post<ViewToken>("/admin/view-token", { project_id: projectId });
  },

  // ---- plans / billing (Phase 10) ----
  listPlans(): Promise<PlanView[]> {
    return IS_MOCK ? mockApi.listPlans() : http.get<PlanView[]>("/admin/plans");
  },
  getUsage(accountId: string): Promise<UsageView> {
    return IS_MOCK
      ? mockApi.getUsage(accountId)
      : http.get<UsageView>(`/admin/usage?account_id=${encodeURIComponent(accountId)}`);
  },
  async setPlan(accountId: string, planKey: string): Promise<void> {
    if (IS_MOCK) return mockApi.setPlan(accountId, planKey);
    return http.post<void>(`/admin/accounts/${encodeURIComponent(accountId)}/plan`, { plan_key: planKey });
  },
  async createAccount(name: string): Promise<AccountCreated> {
    if (IS_MOCK) return mockApi.createAccount(name);
    return http.post<AccountCreated>("/admin/accounts", { name });
  },
  getActiveAccountId(): string | null {
    return IS_MOCK ? mockApi.getActiveAccountId() : activeAccountId;
  },
};
